import React, { useCallback } from 'react';
import PillList from './PillList';
import useFilterConfigurator from './hooks/useFilterConfigurator';
import useFilterLookups from '@/hooks/useFilterLookups';
import {
  AppliedFilter,
  FilterArrayItem,
  ListItem,
} from '../ControlTower/types';

interface AppliedFilterPillsProps {
  lookups: ReturnType<typeof useFilterLookups>;
  setOpenedFromPill: (opened: boolean) => void;
  setEditingFilterId?: (id: string) => void;
  onResetAll?: () => void;
  hasSelectionFilters?: boolean;
  openSubmenuAt?: (filterBy: string, anchorEl: EventTarget) => void;
  appliedFilters?: AppliedFilter[];
  removeAppliedFilter: (filter: AppliedFilter | AppliedFilter[]) => void;
  filterTitle?: string;
  show?: boolean;
  setIsFilterOpen: (open: boolean) => void;
  setSecondaryFiltersDDL: (items: ListItem[]) => void;
  setSecondaryFiltersSource: (items: ListItem[]) => void;
  setOptionsLoading?: (loading: boolean) => void;
  setIsDatePicker: (isDate: boolean) => void;
  setFilterTitle: (title: string) => void;
  secondFilterDisplay?: boolean;
  filterBy?: string;
  setSecondFilterDisplay: (display: boolean) => void;
  setFilterBy: (filterBy: string) => void;
  filterArray: FilterArrayItem[];
  onOpen?: () => void;
  isSaved?: boolean;
  showSaveFilterSetLink?: boolean;
  blockLayout?: boolean;
  className?: string;
  savedFilterKey?: string;
}

const AppliedFilterPills: React.FC<AppliedFilterPillsProps> = ({
  lookups,
  setOpenedFromPill,
  setEditingFilterId,
  onResetAll,
  hasSelectionFilters,
  openSubmenuAt,
  appliedFilters,
  removeAppliedFilter,
  show,
  setIsFilterOpen,
  setSecondaryFiltersDDL,
  setSecondaryFiltersSource,
  setOptionsLoading,
  setIsDatePicker,
  setFilterTitle,
  secondFilterDisplay,
  filterBy,
  setSecondFilterDisplay,
  setFilterBy,
  filterArray,
  onOpen,
  isSaved,
  showSaveFilterSetLink = true,
  blockLayout = false,
  className = '',
  savedFilterKey,
}) => {
  const configureForFilterKey = useFilterConfigurator({
    setIsFilterOpen,
    setSecondaryFiltersDDL,
    setSecondaryFiltersSource,
    setOptionsLoading,
    setIsDatePicker,
    setFilterTitle,
    savedFilterKey,
    lookups,
  });

  const onPillClick = useCallback(
    (fb: string, isGroup: boolean, anchorEl?: EventTarget, id?: string) => {
      if (
        !isGroup ||
        id?.toLowerCase() === 'archived' ||
        id?.toLowerCase() === 'link_code'
      ) {
        return;
      }

      setOpenedFromPill(true);
      setEditingFilterId?.(id);

      if (secondFilterDisplay && filterBy === fb) {
        setSecondFilterDisplay(false);
        setIsFilterOpen(false);
        return;
      }

      // Keep a dropdown overlay mounted across filter switches so the table
      // behind does not flash. Submenu remounts via filterBy key changes.
      configureForFilterKey(fb);
      setFilterBy(fb);
      setSecondFilterDisplay(true);
      setIsFilterOpen(false);

      if (anchorEl) {
        openSubmenuAt?.(fb, anchorEl);
      }
    },
    [
      setEditingFilterId,
      setOpenedFromPill,
      secondFilterDisplay,
      filterBy,
      setSecondFilterDisplay,
      configureForFilterKey,
      setFilterBy,
      openSubmenuAt,
      setIsFilterOpen,
    ]
  );

  if (show === false) return null;

  return (
    <PillList
      onResetAll={onResetAll}
      hasSelectionFilters={hasSelectionFilters}
      appliedFilters={appliedFilters}
      removeAppliedFilter={removeAppliedFilter}
      onPillClick={onPillClick}
      filterArray={filterArray}
      onOpen={onOpen}
      showSaveFilterSetLink={showSaveFilterSetLink}
      isSaved={isSaved}
      blockLayout={blockLayout}
      className={className}
    />
  );
};

export default AppliedFilterPills;
